import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody, CardHeader } from "reactstrap";
import { Button } from "react-bootstrap";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import SelectedTab from "../../../../Components/Common/Tabs/SelectedTabs/SelectedTab";
import {
  resetFormState,
  setNumericValidError,
  setSelectValidError,
} from "../../../../slices/common/reducer";
import {
  changeActiveTabs,
  resetVariantModalState,
  setDetailsData,
  setProductAttributesData,
} from "../../../../slices/orders/orderSlice";
import ProductVariant from "../../Orders/SelectedOrders/ProductVariant";
import { showToast } from "../../../../common/Toaster/Toaster";
import { quoteEditTabs } from "../Json/QuotesJson";
import useCreateQuote from "../Hook/useCreateQuote";
import useUpdateQuote from "../Hook/useUpdateQuote";
import useConvertQuote from "../Hook/useConvertQuote";
import useDownloadQuote from "../Hook/useDownloadQuote";
import useSendQuote from "../Hook/useSendQuote";
import useSendQuoteLink from "../Hook/useSendQuoteLink";
import useQuoteDetails from "../Hook/useQuoteDetails";
import SendQuoteModal from "../Modal/SendQuoteModal";
import ConvertToOrderModal from "../Modal/ConvertToOrderModal";
import Details from "./Details";
import Quotes from "./Quotes";
import "../../Orders/Order.scss";
import "./Quotes.scss";

const SelectedQuotes = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const editMode = Boolean(id);
  // Set once Save is clicked in the attributes modal; the saved-quote layout
  // shows from then on, even before the create response arrives.
  const [isQuoteSaved, setIsQuoteSaved] = useState(false);
  const hasQuote = editMode || isQuoteSaved;

  const activeTab = useSelector(
    (state) => state.order.selectedOrders.activeTabs
  );
  const formState = useSelector((state) => state.common.formRendererState);
  const detailsData = useSelector(
    (state) => state.order.selectedOrders.detailsData
  );
  const productVariantModalState = useSelector(
    (state) => state.order.productVariantModalState
  );
  const productAttributeData = useSelector(
    (state) => state.order.productAttributeData
  );
  // The entity read response may key the id either way
  const quoteId = detailsData.entityid ?? detailsData.entity_id;

  const { refetch: reloadQuote } = useQuoteDetails(id);
  const { mutate: createQuote, isPending: isCreating } = useCreateQuote();
  const { mutate: updateQuote, isPending: isUpdating } = useUpdateQuote();
  const { mutate: convertQuote, isPending: isConverting } = useConvertQuote();
  const { mutate: downloadQuote } = useDownloadQuote();
  const { mutate: sendQuote, isPending: isSendingQuote } = useSendQuote();
  const { mutate: sendQuoteLink, isPending: isSendingLink } =
    useSendQuoteLink();
  const [showSendQuote, setShowSendQuote] = useState(false);
  const [openAttributesSignal, setOpenAttributesSignal] = useState(0);
  const [showConvert, setShowConvert] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(resetFormState());
      dispatch(setProductAttributesData([]));
      dispatch(resetVariantModalState());
      dispatch(changeActiveTabs("details"));
      dispatch(setDetailsData({}));
    };
  }, [dispatch]);

  const breadcrumbItems = editMode
    ? [
        { title: "Orders", link: "" },
        { title: "Quotes", link: "/order/quotes" },
        { title: `${detailsData.code || ""}`, link: "" },
      ]
    : [
        { title: "Orders", link: "" },
        { title: "Quotes", link: "/order/quotes" },
        { title: "New Quote", link: "/order/quotes/create" },
      ];

  // Quote payload built from the Details form and the attributes selection
  // Re-Quote edits land on each product's entry ({ [productId]: {...} }), while
  // variant still holds the copy loaded from getQuote - so a saved quote sends
  // the edited entries (the server works the premium out from them again)
  const getEditedVariant = () => {
    const { oem, variant } = productVariantModalState || {};
    const edited = {};
    (productAttributeData || []).forEach(({ entityid }) => {
      const values = productVariantModalState?.[entityid];
      if (values && typeof values === "object") {
        edited[entityid] = { ...values, ...(oem && { oem }) };
      }
    });
    return Object.keys(edited).length ? edited : variant;
  };

  const getQuotePayload = () => ({
    ...formState,
    ...(editMode && { entityid: quoteId }),
    oem: productVariantModalState?.oem,
    gross_amount: productVariantModalState?.finalPremium,
    cover_amount: productVariantModalState?.coverAmount,
    variant_json: JSON.stringify(
      editMode ? getEditedVariant() : productVariantModalState?.variant
    ),
  });

  const handleDownloadQuote = () => downloadQuote(getQuotePayload());
  const handleSendQuote = () => setShowSendQuote(true);

  const handleSendQuoteSubmit = (recipient) => {
    sendQuote(
      { ...getQuotePayload(), ...recipient },
      { onSuccess: () => setShowSendQuote(false) }
    );
  };

  const handleSendLink = () => {
    if (quoteId && !isSendingLink) {
      sendQuoteLink(quoteId);
    }
  };

  // A saved quote reloads with getQuote before the attributes modal opens, so
  // its fields show the variant_json from the response
  const loadSavedQuote = editMode ? reloadQuote : undefined;

  // A converted quote is final - its attributes can no longer change
  const isConverted = detailsData.status === "CONVERTED";

  // Re-Quote: open the attributes modal from whichever tab is showing
  const handleReQuote = () => {
    if (isConverted) return;
    const openAttributes = () =>
      setOpenAttributesSignal((signal) => signal + 1);
    if (!loadSavedQuote) {
      openAttributes();
      return;
    }
    loadSavedQuote().finally(openAttributes);
  };

  const handleConvertToOrder = () => setShowConvert(true);

  const handleConvertNext = (customerId) => {
    if (!quoteId) {
      showToast("Quote is not saved yet, please try again shortly.", "error");
      return;
    }
    convertQuote(
      { quoteId, customerId },
      {
        onSuccess: (data) => {
          setShowConvert(false);
          if (data?.entityid) {
            navigate(`/order/orders/update/${data.entityid}`);
          }
        },
      }
    );
  };

  // Save in the attributes modal creates the quote; on a saved quote (Re-Quote)
  // it updates that quote's variant_json, insurer and premium instead
  const handleSaveQuote = () => {
    if (isCreating || isUpdating || !productVariantModalState?.oem) return;
    if (editMode) {
      updateQuote({ ...getQuotePayload(), status: "ACTIVE" });
      return;
    }
    setIsQuoteSaved(true);
    dispatch(changeActiveTabs("details"));
    createQuote({ ...getQuotePayload(), status: "ACTIVE" });
  };

  // Save on a new quote: Details fields first, then the attributes selection
  const handleSave = () => {
    if (isCreating) return;
    if (!formState.ag_id || !formState.product_type || !formState.product_id) {
      dispatch(setSelectValidError(true));
      dispatch(setNumericValidError(true));
      showToast("Please fill all required fields before proceeding.", "error");
      return;
    }
    dispatch(setSelectValidError(false));
    dispatch(setNumericValidError(false));
    if (!productVariantModalState?.oem) {
      showToast(
        "Please select the required attributes before proceeding.",
        "error"
      );
      return;
    }
    setIsQuoteSaved(true);
    createQuote({ ...getQuotePayload(), status: "ACTIVE" });
  };

  const showDetails = !hasQuote || activeTab === "details";

  return (
    <div className="page-content">
      <BreadCrumb items={breadcrumbItems} />

      <Card>
        {hasQuote && (
          <CardHeader>
            <SelectedTab
              tabs={quoteEditTabs}
              activeTab={activeTab}
              changeActiveTab={(tab) => dispatch(changeActiveTabs(tab))}
            />
          </CardHeader>
        )}
        <CardBody className="order-details">
          <div className="selected-tab-bottom-content">
            {showDetails && (
              <>
                <div className="quote-section-title">Details</div>
                <Details />
                <div className="quote-section-title">Product</div>
              </>
            )}
            {/* One instance for both tabs, so a Re-Quote signal is not
                replayed when the tab changes; on Quotes only its modal shows */}
            <div className={showDetails ? "quote-product-section" : undefined}>
              <ProductVariant
                nextLabel="Next"
                resetOnClose
                inlineHeader
                isQuote
                onBeforeOpenAttributes={loadSavedQuote}
                attributesDisabled={isConverted}
                modalOnly={!showDetails}
                openAttributesSignal={openAttributesSignal}
                onSaveQuote={handleSaveQuote}
                onDownloadQuote={handleDownloadQuote}
                onSendQuote={handleSendQuote}
              />
            </div>
            {hasQuote && activeTab === "quotes" && <Quotes />}
          </div>
          <div className="d-flex justify-content-end mt-4 footer-buttons">
            {hasQuote ? (
              <>
                <Button
                  className="primary-outline me-2"
                  onClick={handleDownloadQuote}
                >
                  Download Quote
                </Button>
                <Button
                  className="primary-outline me-2"
                  onClick={handleSendQuote}
                >
                  Send Quote
                </Button>
                <Button
                  className="primary-outline me-2"
                  onClick={handleReQuote}
                  disabled={isConverted}
                >
                  Re-Quote
                </Button>
                <Button
                  onClick={handleConvertToOrder}
                  disabled={isConverting || isConverted}
                >
                  {isConverting ? "Converting..." : "Convert to Order"}
                </Button>
              </>
            ) : (
              <>
                <Button
                  className="primary-outline me-2"
                  disabled
                  onClick={handleSendLink}
                >
                  Send Link
                </Button>
                <Button onClick={handleSave} disabled={isCreating}>
                  {isCreating ? "Creating..." : "Save"}
                </Button>
              </>
            )}
          </div>
        </CardBody>
      </Card>
      <SendQuoteModal
        show={showSendQuote}
        onHide={() => setShowSendQuote(false)}
        onSend={handleSendQuoteSubmit}
        isSending={isSendingQuote}
      />
      <ConvertToOrderModal
        show={showConvert}
        onHide={() => setShowConvert(false)}
        onNext={handleConvertNext}
        isConverting={isConverting}
      />
    </div>
  );
};

export default SelectedQuotes;
